import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../../../contexts/AuthContext';
import { useTheme } from '../../../contexts/ThemeContext';
import { toast } from 'react-hot-toast';
import { 
  Settings,
  Brain,
  Save,
  ArrowLeft,
  TestTube,
  Eye,
  EyeOff,
  AlertTriangle,
  CheckCircle,
  Loader,
  MessageSquare,
  Target,
  BarChart3,
  Cpu,
  FileText,
  Sliders,
  Key,
  Users,
  Info,
  HelpCircle
} from 'lucide-react';

const TenantConfiguration = ({ tenantCode: propTenantCode, isModal = false }) => {
  const { tenantCode: paramTenantCode } = useParams();
  const navigate = useNavigate();
  const { hasRole, user, loading: authLoading } = useAuth();
  const { setDarkTheme } = useTheme();
  
  // Use prop tenantCode if in modal mode, otherwise use URL param
  const tenantCode = isModal ? propTenantCode : paramTenantCode;
  

  
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [activeTab, setActiveTab] = useState('models');
  const [showPrompts, setShowPrompts] = useState({});
  const [showHelp, setShowHelp] = useState({});
  
  // AI Services and Models from backend
  const [aiServices, setAiServices] = useState([]);
  const [aiDefaults, setAiDefaults] = useState({});
  const [apiKeys, setApiKeys] = useState({});
  
  // Tenant info
  const [tenantInfo, setTenantInfo] = useState(null);
  
  // AI Configuration
  const [aiConfig, setAiConfig] = useState({
    models: {
      crmAnalysis: { service: 'openai', model: 'gpt-4o-mini', enabled: true },
      crmAssistant: { service: 'openai', model: 'gpt-4o-mini', enabled: true },
      powerSales: { service: 'openai', model: 'gpt-4o-mini', enabled: true },
      businessCardScanner: { service: 'openai', model: 'gpt-4o', enabled: true, fallbackToOCR: true }
    },
    prompts: {
      crmAnalysis: {
        systemPrompt: 'You are a CRM analysis assistant. Analyze data and provide insights.',
        temperature: 0.7,
        maxTokens: 1000,
        topP: 1.0
      },
      crmAssistant: {
        systemPrompt: 'You are a helpful AI assistant for customer support.',
        temperature: 0.8,
        maxTokens: 1500,
        topP: 1.0
      },
      powerSales: {
        systemPrompt: 'You are a sales assistant. Help with sales activities and customer engagement.',
        temperature: 0.9,
        maxTokens: 2000,
        topP: 1.0
      }
    },
    apiKeys: {
      openai: {
        apiKey: '',
        enabled: false
      },
      gemini: {
        apiKey: '',
        enabled: false
      },
      openrouter: {
        apiKey: '',
        enabled: false
      },
      huggingface: {
        apiKey: '',
        enabled: false
      },
      pinecone: {
        apiKey: '',
        enabled: false
      },
      clearbit: {
        apiKey: '',
        enabled: false
      },
      hunter: {
        apiKey: '',
        enabled: false
      },
      fullcontact: {
        apiKey: '',
        enabled: false
      },
      geocoding: {
        apiKey: '',
        enabled: false
      }
    },
    lineChannels: {
      SalesBuddy: {
        accessToken: '',
        channelSecret: '',
        enabled: false
      },
      SalesAssist: {
        accessToken: '',
        channelSecret: '',
        enabled: false
      }
    },
    parameters: {
      rateLimits: {
        requestsPerMinute: 60,
        requestsPerHour: 1000
      },
      security: {
        enableContentFiltering: true,
        logConversations: true
      }
    }
  });

  // Check if user is super admin
  const isSuperAdmin = hasRole(['Admin']);

  // Check if API key is configured and enabled for a service
  const hasApiKey = (serviceName) => {
    // Handle undefined or null serviceName
    if (!serviceName) {
      return false;
    }
    
    // OCR/Tesseract doesn't need API key
    if (serviceName === 'ocr' || serviceName === 'tesseract') {
      return true;
    }
    
    // Map service names to API key names
    const serviceToApiKeyMap = {
      'openai': 'openai',
      'gemini': 'gemini',
      'openrouter': 'openrouter',
      'huggingface': 'huggingface',
      'llava': 'huggingface'
    };
    
    const serviceNameLower = serviceName.toLowerCase();
    const apiKeyName = serviceToApiKeyMap[serviceNameLower];
    if (!apiKeyName) return false;
    
    const apiKeyConfig = apiKeys[apiKeyName];
    return apiKeyConfig && apiKeyConfig.enabled && apiKeyConfig.apiKey && apiKeyConfig.apiKey.trim() !== '';
  };

  // Get filtered services based on available API keys
  // applicationKey: specify application to determine if OCR should be included
  const getAvailableServices = (applicationKey = null) => {
    if (!aiServices || aiServices.length === 0) {
      return [];
    }
    
    return aiServices.filter(service => {
      if (!service || !service.service) return false;
      
      const serviceName = service.service.toLowerCase();
      
      // OCR/Tesseract only available for Business Card Scanner
      if (serviceName === 'ocr' || serviceName === 'tesseract') {
        return applicationKey === 'businessCardScanner';
      }
      
      return hasApiKey(service.service);
    });
  };

  // Load AI models configuration from backend
  const loadAiModelsConfig = useCallback(async () => {
    try {
      const response = await fetch(`/api/ai-config/models?t=${Date.now()}`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Cache-Control': 'no-cache'
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        console.log('AI Models API Response:', data);
        console.log('Services count:', data.data?.services?.length || 0);
        setAiServices(data.data.services || []);
        setAiDefaults(data.data.defaults || {});
      } else {
        console.error('AI Models API failed with status:', response.status);
        throw new Error('Failed to load AI models configuration');
      }
    } catch (error) {
      console.error('Failed to load AI models configuration:', error);
      // Don't show error toast for this, as it's not critical
    }
  }, []);

  const loadTenantConfiguration = useCallback(async () => {
    try {
      setLoading(true);
      console.log('Loading tenant configuration for:', tenantCode);
      
      // Load tenant info
      const tenantResponse = await fetch(`/api/ai-config/admin/tenants/${tenantCode}`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
      
      if (tenantResponse.ok) {
        const tenantData = await tenantResponse.json();
        console.log('Tenant data loaded:', tenantData);
        setTenantInfo(tenantData);
      } else {
        console.error('Failed to load tenant info:', tenantResponse.status, tenantResponse.statusText);
      }
      
      // Load AI configuration
      const configResponse = await fetch(`/api/ai-config/admin/tenants/${tenantCode}/ai-config`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
      
      if (configResponse.ok) {
        const configData = await configResponse.json();
        console.log('AI config loaded:', configData);
        setAiConfig(prev => ({ ...prev, ...configData }));
        
        // Extract API keys configuration
        if (configData.apiKeys) {
          setApiKeys(configData.apiKeys);
        }
      } else {
        console.error('Failed to load AI config:', configResponse.status, configResponse.statusText);
        // Use default config if loading fails
        console.log('Using default AI configuration');
      }
      
    } catch (error) {
      console.error('Failed to load tenant configuration:', error);
      toast.error('Failed to load tenant configuration');
    } finally {
      console.log('Loading completed');
      setLoading(false);
    }
  }, [tenantCode]);

  useEffect(() => {
    // Wait for auth to load before making decisions
    if (authLoading) {
      return;
    }
    
    // In modal mode, skip navigation-based auth checks (parent handles it)
    if (!isModal && !isSuperAdmin) {
      navigate('/dashboard');
      return;
    }
    
    // Set dark theme as default for admin pages
    setDarkTheme();
    
    if (tenantCode) {
      // Load both AI models config and tenant config
      Promise.all([
        loadAiModelsConfig(),
        loadTenantConfiguration()
      ]);
    } else {
      setLoading(false);
    }
  }, [tenantCode, isSuperAdmin, navigate, loadTenantConfiguration, loadAiModelsConfig, authLoading, user, isModal, setDarkTheme]);

  const saveTenantConfiguration = async () => {
    try {
      setSaving(true);
      
      const response = await fetch(`/api/ai-config/admin/tenants/${tenantCode}/ai-config`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify(aiConfig)
      });

      if (response.ok) {
        toast.success('Tenant AI configuration saved successfully');
      } else {
        throw new Error('Failed to save configuration');
      }
    } catch (error) {
      console.error('Failed to save tenant configuration:', error);
      toast.error('Failed to save tenant configuration');
    } finally {
      setSaving(false);
    }
  };

  const updateModel = (applicationKey, field, value) => {
    setAiConfig(prev => ({
      ...prev,
      models: {
        ...prev.models,
        [applicationKey]: {
          ...prev.models[applicationKey],
          [field]: value
        }
      }
    }));
  };

  const updatePrompt = (service, field, value) => {
    setAiConfig(prev => ({
      ...prev,
      prompts: {
        ...prev.prompts,
        [service]: {
          ...prev.prompts[service],
          [field]: value
        }
      }
    }));
  };

  const updateParameter = (category, field, value) => {
    setAiConfig(prev => {
      // Handle apiKeys and lineChannels at top level
      if (category === 'apiKeys' || category === 'lineChannels') {
        return {
          ...prev,
          [category]: {
            ...prev[category],
            [field]: value
          }
        };
      }
      // Handle other parameters under parameters object
      return {
        ...prev,
        parameters: {
          ...prev.parameters,
          [category]: {
            ...prev.parameters[category],
            [field]: value
          }
        }
      };
    });
  };

  const togglePromptVisibility = (service) => {
    setShowPrompts(prev => ({
      ...prev,
      [service]: !prev[service]
    }));
  };

  const toggleHelpVisibility = (service) => {
    setShowHelp(prev => ({
      ...prev,
      [service]: !prev[service]
    }));
  };


  // Define metadata for known applications
  const applicationMetadata = React.useMemo(() => ({
    salesBuddy: {
      name: 'SalesBuddy',
      description: 'Friendly CRM assistant with action capabilities - helps manage customer relationships',
      icon: MessageSquare,
      color: 'blue',
      usedBy: [
        { component: 'AI Chat Panel', location: 'All CRM pages (right-side chat panel)', feature: 'Conversational assistant with RAG' },
        { component: 'Line Bot', location: 'Line Messaging Platform', feature: 'Responds to Line messages with CRM context' },
        { component: 'Chat History', location: 'Admin → Chat History', feature: 'View and manage chat interactions' }
      ]
    },
    salesAssist: {
      name: 'SalesAssist',
      description: 'Strategic sales execution partner - focused on closing deals and achieving targets',
      icon: Target,
      color: 'purple',
      usedBy: [
        { component: 'Sales Dashboard', location: 'Dashboard (top-right chat)', feature: 'Text-only sales advisor for strategic insights' },
        { component: 'Opportunities', location: 'Opportunities page chat', feature: 'Deal closing strategies and guidance' },
        { component: 'Reports', location: 'Reports & Analytics', feature: 'Sales performance analysis' }
      ]
    },
    salesAnalysis: {
      name: 'Sales Analysis',
      description: 'AI-powered sales data analysis and insights',
      icon: BarChart3,
      color: 'green',
      usedBy: [
        { component: 'Smart Notes Analysis', location: 'Customer/Lead/Opportunity notes', feature: 'Extracts entities from sales notes' },
        { component: 'Activity Extraction', location: 'All entity pages', feature: 'Automatically creates activities from notes' },
        { component: 'Entity Detection', location: 'Note creation forms', feature: 'Detects customers, leads, opportunities' },
        { component: 'Data Enrichment', location: 'Import & Create wizards', feature: 'Enriches data with AI insights' }
      ]
    },
    businessCardScanner: {
      name: 'Business Card Scanner',
      description: 'AI-powered extraction from business cards (Thai & English)',
      icon: Cpu,
      color: 'orange',
      hasFallback: true,
      usedBy: [
        { component: 'Business Card Upload', location: 'Customers → Add → Business Card', feature: 'Extract contact info from card images' },
        { component: 'Lead Import', location: 'Leads → Import → Business Card', feature: 'Bulk import from business cards' },
        { component: 'Mobile Scanner', location: 'Mobile app (if available)', feature: 'Scan cards on-the-go' }
      ]
    },
    leadGeneration: {
      name: 'Lead Generation',
      description: 'AI-powered lead generation from external sources',
      icon: Users,
      color: 'indigo',
      usedBy: [
        { component: 'Lead Generator', location: 'Leads → Generate Leads', feature: 'Generate leads from LinkedIn, X, websites' },
        { component: 'External Sources', location: 'Leads → Import → External', feature: 'Import from public directories' },
        { component: 'API Integration', location: 'API endpoints', feature: 'Automated lead generation via API' }
      ]
    },
    leadScout: {
      name: 'Lead Scout',
      description: 'AI-powered lead scouting from customer information',
      icon: Target,
      color: 'purple',
      usedBy: [
        { component: 'Lead Scout', location: 'Leads → Scout Leads', feature: 'Find leads from existing customer data' },
        { component: 'Customer Analysis', location: 'Customers page', feature: 'Identify potential leads from customers' },
        { component: 'Opportunity Discovery', location: 'Opportunities', feature: 'Discover new opportunities' }
      ]
    },
    knowledgeAnalysis: {
      name: 'Knowledge Analysis',
      description: 'AI-powered analysis and rewriting of knowledge articles for chat optimization',
      icon: Brain,
      color: 'cyan',
      usedBy: [
        { component: 'Knowledge Base', location: 'Admin → Knowledge Base', feature: 'Auto-rewrites articles for chat use' },
        { component: 'RAG System', location: 'All AI chat features', feature: 'Optimizes content for retrieval' },
        { component: 'Product Catalog', location: 'Knowledge articles', feature: 'Extracts product/service info' },
        { component: 'Auto-Processing', location: 'Background job', feature: 'Triggers on article add/update' }
      ]
    }
  }), []);

  // Dynamically generate aiApplications from aiConfig.models
  const aiApplications = React.useMemo(() => {
    if (!aiConfig?.models) return [];
    
    return Object.keys(aiConfig.models).map(key => {
      const metadata = applicationMetadata[key];
      
      // If we have metadata, use it; otherwise generate defaults
      if (metadata) {
        return {
          key,
          ...metadata
        };
      }
      
      // Generate default metadata for unknown models
      const displayName = key
        .replace(/([A-Z])/g, ' $1') // Add space before capital letters
        .replace(/^./, str => str.toUpperCase()) // Capitalize first letter
        .trim();
      
      return {
        key,
        name: displayName,
        description: `AI model configuration for ${displayName}`,
        icon: Brain,
        color: 'gray'
      };
    });
  }, [aiConfig?.models, applicationMetadata]);

  if (!isModal && !isSuperAdmin) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <AlertTriangle className="mx-auto h-12 w-12 text-yellow-400" />
          <h3 className="mt-2 text-sm font-medium text-gray-900">Access Denied</h3>
          <p className="mt-1 text-sm text-gray-500">
            Super Admin access required.
          </p>
        </div>
      </div>
    );
  }

  if (authLoading || loading || aiServices.length === 0) {
    return (
      <div className={`${isModal ? 'min-h-96' : 'min-h-screen'} flex items-center justify-center`}>
        <div className="text-center">
          <Loader className="mx-auto h-8 w-8 animate-spin text-blue-600" />
          <p className="mt-2 text-sm text-gray-600">
            {authLoading ? 'Loading authentication...' : 
             aiServices.length === 0 ? 'Loading AI models...' : 
             'Loading tenant configuration...'}
          </p>
          <p className="mt-1 text-xs text-gray-500">Tenant Code: {tenantCode}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-4">
          {!isModal && (
            <button
              onClick={() => navigate('/admin/tenants')}
              className="flex items-center space-x-2 px-3 py-2 border border-gray-300 rounded-md hover:bg-gray-50"
            >
              <ArrowLeft className="h-4 w-4" />
              <span>Back to Tenants</span>
            </button>
          )}
          <div>
            <h1 className="text-2xl font-bold text-gray-900 flex items-center space-x-2">
              <Brain className="h-7 w-7 text-blue-600" />
              <span>Tenant AI Configuration</span>
            </h1>
            <p className="text-gray-600 mt-1">
              {tenantInfo ? `${tenantInfo.tenantName} (${tenantInfo.tenantCode})` : tenantCode}
            </p>
          </div>
        </div>
        <button
          onClick={saveTenantConfiguration}
          disabled={saving}
          className="flex items-center space-x-2 px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50"
        >
          {saving ? (
            <Loader className="h-4 w-4 animate-spin" />
          ) : (
            <Save className="h-4 w-4" />
          )}
          <span>{saving ? 'Saving...' : 'Save Configuration'}</span>
        </button>
      </div>

      {/* Tab Navigation */}
      <div className="border-b border-gray-200">
        <nav className="-mb-px flex space-x-8">
          {[
            { id: 'models', label: 'AI Agents', icon: Brain },
            { id: 'prompts', label: 'Agent Prompts', icon: FileText },
            { id: 'parameters', label: 'Parameters', icon: Sliders },
            { id: 'apikeys', label: 'API Keys', icon: Key }
          ].map((tab) => {
            const Icon = tab.icon;
            return (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex items-center space-x-2 py-2 px-1 border-b-2 font-medium text-sm ${
                  activeTab === tab.id
                    ? 'border-blue-500 text-blue-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                <Icon className="h-4 w-4" />
                <span>{tab.label}</span>
              </button>
            );
          })}
        </nav>
      </div>

      {/* Tab Content */}
      <div className="bg-white rounded-lg shadow">
        {/* AI Agents Tab */}
        {activeTab === 'models' && (
          <div className="p-6">
            <div className="mb-6">
              <h2 className="text-lg font-medium text-gray-900 mb-2">
                AI Agent Configuration
                <span className="ml-3 text-sm font-normal text-gray-500">
                  Configure AI agents with standard and custom attributes
                </span>
              </h2>
              <div className="mt-3 p-3 bg-blue-50 border border-blue-200 rounded-md">
                <div className="flex items-start space-x-2">
                  <Info className="h-4 w-4 text-blue-600 mt-0.5 flex-shrink-0" />
                  <div className="flex-1 text-xs text-blue-800">
                    <p className="font-medium mb-1">💡 Quick Guide:</p>
                    <p>Each AI agent serves a specific purpose in the CRM system. Click the <span className="inline-flex items-center px-1 py-0.5 bg-white rounded border border-blue-300 text-blue-600 font-medium">"Show Usage"</span> button on any agent to see which components use it and where to find those features in the application.</p>
                  </div>
                </div>
              </div>
            </div>
            <div className="space-y-6">
              {aiApplications.map((application) => {
                const Icon = application.icon;
                const config = aiConfig.models?.[application.key] || { service: 'openai', model: 'gpt-4o-mini', enabled: true };
                const currentService = aiServices.find(s => s.service === config?.service);
                const availableModels = currentService?.models || [];
                
                // Get only services with API keys (pass application key to determine OCR availability)
                const availableServices = getAvailableServices(application.key);
                
                // Check if current service has API key
                const currentServiceHasApiKey = hasApiKey(config?.service);
                
                // If no services available
                // For Business Card Scanner: show OCR at minimum
                // For others: show empty or first available with API key
                const displayServices = availableServices.length > 0 
                  ? availableServices 
                  : application.key === 'businessCardScanner'
                    ? aiServices.filter(s => s.service === 'ocr' || s.service === 'tesseract')
                    : [];

                // Define standard attributes that are shown separately
                const standardAttributes = ['service', 'model', 'enabled'];
                
                // Get custom attributes (everything except standard ones)
                const customAttributes = Object.keys(config || {}).filter(
                  key => !standardAttributes.includes(key)
                );

                // Helper function to render custom attribute input
                const renderCustomAttribute = (attrKey, attrValue) => {
                  const label = attrKey
                    .replace(/([A-Z])/g, ' $1')
                    .replace(/^./, str => str.toUpperCase())
                    .trim();

                  // Determine input type based on value type
                  if (typeof attrValue === 'boolean') {
                    return (
                      <div key={attrKey} className="flex items-center space-x-2">
                        <input
                          type="checkbox"
                          id={`${application.key}-${attrKey}`}
                          checked={attrValue}
                          onChange={(e) => updateModel(application.key, attrKey, e.target.checked)}
                          className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                        />
                        <label htmlFor={`${application.key}-${attrKey}`} className="text-xs font-medium text-gray-700">
                          {label}
                        </label>
                      </div>
                    );
                  } else if (typeof attrValue === 'number') {
                    return (
                      <div key={attrKey}>
                        <label className="block text-xs font-medium text-gray-700 mb-1">
                          {label}
                        </label>
                        <input
                          type="number"
                          value={attrValue}
                          onChange={(e) => updateModel(application.key, attrKey, parseFloat(e.target.value) || 0)}
                          className="w-full px-2 py-1 text-xs border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500"
                        />
                      </div>
                    );
                  } else if (attrKey.includes('Model') && availableModels.length > 0) {
                    // Model selection dropdown for attributes with "Model" in the name
                    return (
                      <div key={attrKey}>
                        <label className="block text-xs font-medium text-gray-700 mb-1">
                          {label}
                        </label>
                        <select
                          value={attrValue || 'gpt-4o-mini'}
                          onChange={(e) => updateModel(application.key, attrKey, e.target.value)}
                          className="w-full px-2 py-1 text-xs border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500"
                        >
                          {availableModels.map((model) => (
                            <option key={model.value} value={model.value}>
                              {model.label}
                            </option>
                          ))}
                        </select>
                      </div>
                    );
                  } else {
                    // String input for everything else
                    return (
                      <div key={attrKey}>
                        <label className="block text-xs font-medium text-gray-700 mb-1">
                          {label}
                        </label>
                        <input
                          type="text"
                          value={attrValue || ''}
                          onChange={(e) => updateModel(application.key, attrKey, e.target.value)}
                          className="w-full px-2 py-1 text-xs border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500"
                        />
                      </div>
                    );
                  }
                };
                
                return (
                  <div key={application.key} className="border border-gray-200 rounded-lg p-4">
                    {/* Agent Header */}
                    <div className="flex items-center space-x-4 mb-4">
                      <div className={`p-3 rounded-lg bg-${application.color}-100`}>
                        <Icon className={`h-6 w-6 text-${application.color}-600`} />
                      </div>
                      <div className="flex-1">
                        <div className="flex items-center justify-between">
                          <div>
                            <h3 className="text-lg font-medium text-gray-900">{application.name}</h3>
                            <p className="text-sm text-gray-500">{application.description}</p>
                            <div className="mt-2 text-xs text-gray-500">
                              Current: {currentService?.label || 'Unknown'} → {availableModels.find(m => m.value === config?.model)?.label || config?.model || 'N/A'}
                            </div>
                            {!currentServiceHasApiKey && config?.service && (
                              <div className="mt-1 text-xs text-red-600">
                                ⚠️ API key not configured for {currentService?.label || config.service}
                              </div>
                            )}
                          </div>
                          {application.usedBy && application.usedBy.length > 0 && (
                            <button
                              onClick={() => toggleHelpVisibility(application.key)}
                              className="flex items-center space-x-1 px-2 py-1 text-xs border border-blue-300 text-blue-600 rounded-md hover:bg-blue-50"
                              title="Show where this agent is used"
                            >
                              <HelpCircle className="h-3 w-3" />
                              <span>{showHelp[application.key] ? 'Hide' : 'Show'} Usage</span>
                            </button>
                          )}
                        </div>
                      </div>
                    </div>

                    {/* Help Section - Where is this agent used? */}
                    {showHelp[application.key] && application.usedBy && application.usedBy.length > 0 && (
                      <div className="mb-4 p-3 bg-blue-50 border border-blue-200 rounded-md">
                        <div className="flex items-start space-x-2 mb-2">
                          <Info className="h-4 w-4 text-blue-600 mt-0.5 flex-shrink-0" />
                          <div className="flex-1">
                            <h4 className="text-sm font-semibold text-blue-900 mb-2">
                              Components Using This Agent
                            </h4>
                            <div className="space-y-2">
                              {application.usedBy.map((usage, idx) => (
                                <div key={idx} className="text-xs text-blue-800">
                                  <div className="font-medium">📍 {usage.component}</div>
                                  <div className="ml-4 text-blue-700">
                                    <span className="font-medium">Location:</span> {usage.location}
                                  </div>
                                  <div className="ml-4 text-blue-700">
                                    <span className="font-medium">Feature:</span> {usage.feature}
                                  </div>
                                </div>
                              ))}
                            </div>
                          </div>
                        </div>
                      </div>
                    )}

                    {/* Standard Attributes */}
                    <div className="grid grid-cols-3 gap-3 mb-4">
                      {/* AI Service Selection */}
                      <div>
                        <label className="block text-xs font-medium text-gray-700 mb-1">
                          AI Service
                        </label>
                        <select
                          value={config?.service || 'ocr'}
                          onChange={(e) => {
                            updateModel(application.key, 'service', e.target.value);
                            // Reset model when service changes
                            const newService = aiServices.find(s => s.service === e.target.value);
                            const defaultModel = newService?.models[0]?.value || aiDefaults[application.key]?.model || 'gpt-4o-mini';
                            updateModel(application.key, 'model', defaultModel);
                          }}
                          className="w-full px-2 py-1 text-sm border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        >
                          {displayServices.length > 0 ? (
                            displayServices.map((service) => (
                              <option key={service?.service || Math.random()} value={service?.service}>
                                {service?.label || service?.service}
                              </option>
                            ))
                          ) : (
                            <option value="">
                              {application.key === 'businessCardScanner' 
                                ? 'No services available' 
                                : 'No AI services configured - Please add API keys'}
                            </option>
                          )}
                        </select>
                      </div>
                      
                      {/* Model Selection */}
                      <div>
                        <label className="block text-xs font-medium text-gray-700 mb-1">
                          AI Model
                        </label>
                        <select
                          value={config?.model || 'gpt-4o-mini'}
                          onChange={(e) => updateModel(application.key, 'model', e.target.value)}
                          disabled={!config?.service || displayServices.length === 0}
                          className="w-full px-2 py-1 text-sm border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          {availableModels.length > 0 ? (
                            availableModels.map((model) => (
                              <option key={model.value} value={model.value}>
                                {model.label}
                              </option>
                            ))
                          ) : (
                            <option value={config?.model || 'gpt-4o-mini'}>
                              {config?.model || 'Loading models...'}
                            </option>
                          )}
                        </select>
                      </div>

                      {/* Enabled Toggle */}
                      <div>
                        <label className="block text-xs font-medium text-gray-700 mb-1">
                          Status
                        </label>
                        <div className="flex items-center h-8">
                          <input
                            type="checkbox"
                            id={`${application.key}-enabled`}
                            checked={config?.enabled !== false}
                            onChange={(e) => updateModel(application.key, 'enabled', e.target.checked)}
                            className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                          />
                          <label htmlFor={`${application.key}-enabled`} className="ml-2 text-sm text-gray-700">
                            Enabled
                          </label>
                        </div>
                      </div>
                    </div>

                    {/* Custom Attributes */}
                    {customAttributes.length > 0 && (
                      <div className="mt-4 pt-4 border-t border-gray-200">
                        <h4 className="text-sm font-semibold text-gray-700 mb-3 flex items-center">
                          <Settings className="h-4 w-4 mr-2" />
                          Custom Agent Attributes
                        </h4>
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                          {customAttributes.map(attrKey => 
                            renderCustomAttribute(attrKey, config[attrKey])
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* Agent Prompts Tab */}
        {activeTab === 'prompts' && (
          <div className="p-6">
            <h2 className="text-lg font-medium text-gray-900 mb-6">
              Agent System Prompts
              <span className="ml-3 text-sm font-normal text-gray-500">
                Configure prompts and parameters for each AI agent
              </span>
            </h2>
            <div className="space-y-6">
              {aiApplications.map((application) => {
                const Icon = application.icon;
                const config = aiConfig.prompts[application.key];
                
                return (
                  <div key={application.key} className="border border-gray-200 rounded-lg p-6">
                    <div className="flex items-center justify-between mb-4">
                      <div className="flex items-center space-x-4">
                        <div className={`p-3 rounded-lg bg-${application.color}-100`}>
                          <Icon className={`h-6 w-6 text-${application.color}-600`} />
                        </div>
                        <div>
                          <h3 className="text-lg font-medium text-gray-900">{application.name}</h3>
                          <p className="text-sm text-gray-500">{application.description}</p>
                        </div>
                      </div>
                      <button
                        onClick={() => togglePromptVisibility(application.key)}
                        className="flex items-center space-x-2 px-3 py-2 border border-gray-300 rounded-md hover:bg-gray-50"
                      >
                        {showPrompts[application.key] ? (
                          <EyeOff className="h-4 w-4" />
                        ) : (
                          <Eye className="h-4 w-4" />
                        )}
                        <span>{showPrompts[application.key] ? 'Hide' : 'Show'} Prompt</span>
                      </button>
                    </div>

                    {showPrompts[application.key] && (
                      <div className="space-y-4">
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">
                            System Prompt
                          </label>
                          <textarea
                            value={config.systemPrompt}
                            onChange={(e) => updatePrompt(application.key, 'systemPrompt', e.target.value)}
                            rows={6}
                            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                            placeholder="Enter system prompt..."
                          />
                        </div>

                        <div className="grid grid-cols-3 gap-4">
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                              Temperature
                            </label>
                            <input
                              type="number"
                              min="0"
                              max="2"
                              step="0.1"
                              value={config.temperature}
                              onChange={(e) => updatePrompt(application.key, 'temperature', parseFloat(e.target.value))}
                              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                            />
                          </div>

                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                              Max Tokens
                            </label>
                            <input
                              type="number"
                              min="100"
                              max="4000"
                              value={config.maxTokens}
                              onChange={(e) => updatePrompt(application.key, 'maxTokens', parseInt(e.target.value))}
                              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                            />
                          </div>

                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                              Top P
                            </label>
                            <input
                              type="number"
                              min="0"
                              max="1"
                              step="0.1"
                              value={config.topP}
                              onChange={(e) => updatePrompt(application.key, 'topP', parseFloat(e.target.value))}
                              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                            />
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* Parameters Tab */}
        {activeTab === 'parameters' && (
          <div className="p-6">
            <h2 className="text-lg font-medium text-gray-900 mb-6">Advanced Parameters</h2>
            <div className="space-y-6">
              {/* Rate Limits */}
              <div className="border border-gray-200 rounded-lg p-6">
                <h3 className="text-lg font-medium text-gray-900 mb-4">Rate Limits</h3>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Requests per Minute
                    </label>
                    <input
                      type="number"
                      min="1"
                      value={aiConfig.parameters.rateLimits.requestsPerMinute}
                      onChange={(e) => updateParameter('rateLimits', 'requestsPerMinute', parseInt(e.target.value))}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Requests per Hour
                    </label>
                    <input
                      type="number"
                      min="1"
                      value={aiConfig.parameters.rateLimits.requestsPerHour}
                      onChange={(e) => updateParameter('rateLimits', 'requestsPerHour', parseInt(e.target.value))}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    />
                  </div>
                </div>
              </div>

              {/* Security Settings */}
              <div className="border border-gray-200 rounded-lg p-6">
                <h3 className="text-lg font-medium text-gray-900 mb-4">Security Settings</h3>
                <div className="space-y-4">
                  <label className="flex items-center space-x-3">
                    <input
                      type="checkbox"
                      checked={aiConfig.parameters.security.enableContentFiltering}
                      onChange={(e) => updateParameter('security', 'enableContentFiltering', e.target.checked)}
                      className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                    />
                    <div>
                      <span className="text-sm font-medium text-gray-700">Enable Content Filtering</span>
                      <p className="text-xs text-gray-500">Filter inappropriate content in AI responses</p>
                    </div>
                  </label>
                  
                  <label className="flex items-center space-x-3">
                    <input
                      type="checkbox"
                      checked={aiConfig.parameters.security.logConversations}
                      onChange={(e) => updateParameter('security', 'logConversations', e.target.checked)}
                      className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                    />
                    <div>
                      <span className="text-sm font-medium text-gray-700">Log Conversations</span>
                      <p className="text-xs text-gray-500">Keep logs of AI conversations for auditing</p>
                    </div>
                  </label>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* API Keys Tab */}
        {activeTab === 'apikeys' && (
          <div className="p-6">
            <h2 className="text-lg font-medium text-gray-900 mb-6">AI Service API Keys</h2>
            <div className="space-y-6">
              
              {/* AI Services API Keys */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                
                {/* OpenAI */}
                <div className="border border-gray-200 rounded-lg p-4">
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center space-x-3">
                      <div className="p-2 rounded-lg bg-green-100">
                        <Cpu className="h-5 w-5 text-green-600" />
                      </div>
                      <div>
                        <h3 className="font-medium text-gray-900">OpenAI</h3>
                        <p className="text-sm text-gray-500">GPT models</p>
                      </div>
                    </div>
                    <label className="flex items-center">
                      <input
                        type="checkbox"
                        checked={aiConfig.apiKeys.openai.enabled}
                        onChange={(e) => updateParameter('apiKeys', 'openai', {
                          ...aiConfig.apiKeys.openai,
                          enabled: e.target.checked
                        })}
                        className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                      />
                      <span className="ml-2 text-sm font-medium text-gray-700">Enabled</span>
                    </label>
                  </div>
                  <input
                    type="password"
                    value={aiConfig.apiKeys.openai.apiKey}
                    onChange={(e) => updateParameter('apiKeys', 'openai', {
                      ...aiConfig.apiKeys.openai,
                      apiKey: e.target.value
                    })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
                    placeholder="sk-..."
                  />
                </div>

                {/* Gemini */}
                <div className="border border-gray-200 rounded-lg p-4">
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center space-x-3">
                      <div className="p-2 rounded-lg bg-blue-100">
                        <Brain className="h-5 w-5 text-blue-600" />
                      </div>
                      <div>
                        <h3 className="font-medium text-gray-900">Google Gemini</h3>
                        <p className="text-sm text-gray-500">Gemini models</p>
                      </div>
                    </div>
                    <label className="flex items-center">
                      <input
                        type="checkbox"
                        checked={aiConfig.apiKeys.gemini.enabled}
                        onChange={(e) => updateParameter('apiKeys', 'gemini', {
                          ...aiConfig.apiKeys.gemini,
                          enabled: e.target.checked
                        })}
                        className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                      />
                      <span className="ml-2 text-sm font-medium text-gray-700">Enabled</span>
                    </label>
                  </div>
                  <input
                    type="password"
                    value={aiConfig.apiKeys.gemini.apiKey}
                    onChange={(e) => updateParameter('apiKeys', 'gemini', {
                      ...aiConfig.apiKeys.gemini,
                      apiKey: e.target.value
                    })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
                    placeholder="AI..."
                  />
                </div>

                {/* OpenRouter */}
                <div className="border border-gray-200 rounded-lg p-4">
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center space-x-3">
                      <div className="p-2 rounded-lg bg-purple-100">
                        <BarChart3 className="h-5 w-5 text-purple-600" />
                      </div>
                      <div>
                        <h3 className="font-medium text-gray-900">OpenRouter</h3>
                        <p className="text-sm text-gray-500">Multiple models</p>
                      </div>
                    </div>
                    <label className="flex items-center">
                      <input
                        type="checkbox"
                        checked={aiConfig.apiKeys.openrouter.enabled}
                        onChange={(e) => updateParameter('apiKeys', 'openrouter', {
                          ...aiConfig.apiKeys.openrouter,
                          enabled: e.target.checked
                        })}
                        className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                      />
                      <span className="ml-2 text-sm font-medium text-gray-700">Enabled</span>
                    </label>
                  </div>
                  <input
                    type="password"
                    value={aiConfig.apiKeys.openrouter.apiKey}
                    onChange={(e) => updateParameter('apiKeys', 'openrouter', {
                      ...aiConfig.apiKeys.openrouter,
                      apiKey: e.target.value
                    })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
                    placeholder="sk-or-..."
                  />
                </div>

                {/* Hugging Face */}
                <div className="border border-gray-200 rounded-lg p-4">
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center space-x-3">
                      <div className="p-2 rounded-lg bg-yellow-100">
                        <Target className="h-5 w-5 text-yellow-600" />
                      </div>
                      <div>
                        <h3 className="font-medium text-gray-900">Hugging Face</h3>
                        <p className="text-sm text-gray-500">Open source models</p>
                      </div>
                    </div>
                    <label className="flex items-center">
                      <input
                        type="checkbox"
                        checked={aiConfig.apiKeys.huggingface.enabled}
                        onChange={(e) => updateParameter('apiKeys', 'huggingface', {
                          ...aiConfig.apiKeys.huggingface,
                          enabled: e.target.checked
                        })}
                        className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                      />
                      <span className="ml-2 text-sm font-medium text-gray-700">Enabled</span>
                    </label>
                  </div>
                  <input
                    type="password"
                    value={aiConfig.apiKeys.huggingface.apiKey}
                    onChange={(e) => updateParameter('apiKeys', 'huggingface', {
                      ...aiConfig.apiKeys.huggingface,
                      apiKey: e.target.value
                    })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
                    placeholder="hf_..."
                  />
                </div>

              </div>

              {/* Vector Database & Enrichment Services */}
              <div className="border-t border-gray-200 pt-6">
                <h3 className="text-md font-medium text-gray-900 mb-4">Vector Database & Enrichment Services</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  
                  {/* Pinecone */}
                  <div className="border border-gray-200 rounded-lg p-3">
                    <div className="flex items-center justify-between mb-2">
                      <h4 className="font-medium text-gray-900 text-sm">Pinecone</h4>
                      <label className="flex items-center">
                        <input
                          type="checkbox"
                          checked={aiConfig.apiKeys.pinecone.enabled}
                          onChange={(e) => updateParameter('apiKeys', 'pinecone', {
                            ...aiConfig.apiKeys.pinecone,
                            enabled: e.target.checked
                          })}
                          className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                        />
                        <span className="ml-1 text-xs text-gray-600">Enabled</span>
                      </label>
                    </div>
                    <input
                      type="password"
                      value={aiConfig.apiKeys.pinecone.apiKey}
                      onChange={(e) => updateParameter('apiKeys', 'pinecone', {
                        ...aiConfig.apiKeys.pinecone,
                        apiKey: e.target.value
                      })}
                      className="w-full px-2 py-1 border border-gray-300 rounded text-xs"
                      placeholder="Pinecone API key"
                    />
                  </div>

                  {/* Clearbit */}
                  <div className="border border-gray-200 rounded-lg p-3">
                    <div className="flex items-center justify-between mb-2">
                      <h4 className="font-medium text-gray-900 text-sm">Clearbit</h4>
                      <label className="flex items-center">
                        <input
                          type="checkbox"
                          checked={aiConfig.apiKeys.clearbit.enabled}
                          onChange={(e) => updateParameter('apiKeys', 'clearbit', {
                            ...aiConfig.apiKeys.clearbit,
                            enabled: e.target.checked
                          })}
                          className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                        />
                        <span className="ml-1 text-xs text-gray-600">Enabled</span>
                      </label>
                    </div>
                    <input
                      type="password"
                      value={aiConfig.apiKeys.clearbit.apiKey}
                      onChange={(e) => updateParameter('apiKeys', 'clearbit', {
                        ...aiConfig.apiKeys.clearbit,
                        apiKey: e.target.value
                      })}
                      className="w-full px-2 py-1 border border-gray-300 rounded text-xs"
                      placeholder="Clearbit API key"
                    />
                  </div>

                  {/* Hunter */}
                  <div className="border border-gray-200 rounded-lg p-3">
                    <div className="flex items-center justify-between mb-2">
                      <h4 className="font-medium text-gray-900 text-sm">Hunter.io</h4>
                      <label className="flex items-center">
                        <input
                          type="checkbox"
                          checked={aiConfig.apiKeys.hunter.enabled}
                          onChange={(e) => updateParameter('apiKeys', 'hunter', {
                            ...aiConfig.apiKeys.hunter,
                            enabled: e.target.checked
                          })}
                          className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                        />
                        <span className="ml-1 text-xs text-gray-600">Enabled</span>
                      </label>
                    </div>
                    <input
                      type="password"
                      value={aiConfig.apiKeys.hunter.apiKey}
                      onChange={(e) => updateParameter('apiKeys', 'hunter', {
                        ...aiConfig.apiKeys.hunter,
                        apiKey: e.target.value
                      })}
                      className="w-full px-2 py-1 border border-gray-300 rounded text-xs"
                      placeholder="Hunter API key"
                    />
                  </div>

                  {/* FullContact */}
                  <div className="border border-gray-200 rounded-lg p-3">
                    <div className="flex items-center justify-between mb-2">
                      <h4 className="font-medium text-gray-900 text-sm">FullContact</h4>
                      <label className="flex items-center">
                        <input
                          type="checkbox"
                          checked={aiConfig.apiKeys.fullcontact.enabled}
                          onChange={(e) => updateParameter('apiKeys', 'fullcontact', {
                            ...aiConfig.apiKeys.fullcontact,
                            enabled: e.target.checked
                          })}
                          className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                        />
                        <span className="ml-1 text-xs text-gray-600">Enabled</span>
                      </label>
                    </div>
                    <input
                      type="password"
                      value={aiConfig.apiKeys.fullcontact.apiKey}
                      onChange={(e) => updateParameter('apiKeys', 'fullcontact', {
                        ...aiConfig.apiKeys.fullcontact,
                        apiKey: e.target.value
                      })}
                      className="w-full px-2 py-1 border border-gray-300 rounded text-xs"
                      placeholder="FullContact API key"
                    />
                  </div>

                  {/* Geocoding */}
                  <div className="border border-gray-200 rounded-lg p-3">
                    <div className="flex items-center justify-between mb-2">
                      <h4 className="font-medium text-gray-900 text-sm">Geocoding</h4>
                      <label className="flex items-center">
                        <input
                          type="checkbox"
                          checked={aiConfig.apiKeys.geocoding.enabled}
                          onChange={(e) => updateParameter('apiKeys', 'geocoding', {
                            ...aiConfig.apiKeys.geocoding,
                            enabled: e.target.checked
                          })}
                          className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                        />
                        <span className="ml-1 text-xs text-gray-600">Enabled</span>
                      </label>
                    </div>
                    <input
                      type="password"
                      value={aiConfig.apiKeys.geocoding.apiKey}
                      onChange={(e) => updateParameter('apiKeys', 'geocoding', {
                        ...aiConfig.apiKeys.geocoding,
                        apiKey: e.target.value
                      })}
                      className="w-full px-2 py-1 border border-gray-300 rounded text-xs"
                      placeholder="Geocoding API key"
                    />
                  </div>

                </div>
              </div>

              {/* API Keys Configuration Note */}
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <div className="flex">
                  <Key className="h-5 w-5 text-blue-400 mt-0.5" />
                  <div className="ml-3">
                    <h3 className="text-sm font-medium text-blue-800">
                      API Keys Management
                    </h3>
                    <div className="mt-2 text-sm text-blue-700">
                      <ul className="list-disc list-inside space-y-1">
                        <li>Each tenant can use their own API keys for AI services</li>
                        <li>API keys are stored securely in tenant configuration files</li>
                        <li>Enable only the services you have valid API keys for</li>
                        <li>Disabled services will fallback to global environment variables</li>
                        <li>Test each service after configuration to ensure proper functionality</li>
                      </ul>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default TenantConfiguration;